import { useEffect, useState } from 'react';
import { 
  FiFileText, 
  FiClock, 
  FiCheckCircle, 
  FiUpload,
  FiTrendingUp,
  FiActivity
} from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { formatDateShort } from '../utils/dateFormat';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/stats?period=all');
      if (response.data.success && response.data.data) {
        console.log('Stats received:', response.data.data);
        setStats(response.data.data);
      } else {
        toast.error('Dữ liệu thống kê không hợp lệ');
      }
    } catch (error) {
      console.error('Stats fetch error:', error);
      toast.error('Không thể tải thống kê: ' + (error.response?.data?.error?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
        <div className="w-7 h-7 border-2 border-slate-200 border-t-primary rounded-full animate-spin"></div>
        <p className="text-slate-500 text-sm">Đang tải dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center p-6 text-red-600 text-sm">Không có dữ liệu</div>;
  }

  const statCards = [
    {
      key: 'total',
      title: 'Tổng số ca chụp',
      value: stats.total || 0,
      icon: FiFileText,
      color: '#3b82f6',
      bgGradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
    },
    {
      key: 'scheduled',
      title: 'Đã lên lịch',
      value: stats.byStatus?.scheduled || 0,
      icon: FiClock,
      color: '#3b82f6',
      bgGradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
    },
    {
      key: 'in_progress',
      title: 'Đang thực hiện',
      value: stats.byStatus?.in_progress || 0,
      icon: FiActivity,
      color: '#f59e0b',
      bgGradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
    },
    {
      key: 'completed',
      title: 'Hoàn thành',
      value: stats.byStatus?.completed || 0,
      icon: FiCheckCircle,
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
    },
    {
      key: 'cancelled',
      title: 'Đã hủy',
      value: stats.byStatus?.cancelled || 0,
      icon: FiFileText,
      color: '#ef4444',
      bgGradient: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
    },
    {
      key: 'synced',
      title: 'Đã đồng bộ',
      value: stats.byStatus?.synced || 0,
      icon: FiUpload,
      color: '#8b5cf6',
      bgGradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* Stat cards - align style with CloudDashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-lg p-3 border border-slate-200 relative overflow-hidden shadow-sm hover:bg-slate-50 transition-colors"
            >
              <div
                className="absolute top-0 left-0 right-0 h-0.5"
                style={{ background: card.color }}
              ></div>
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h3 className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-1">
                    {card.title}
                  </h3>
                  <div className="text-2xl font-bold text-slate-900">{card.value}</div>
                  {card.key === 'completed' && stats.completionRate !== undefined && (
                    <div className="mt-1 text-xs font-semibold text-emerald-600">
                      Tỷ lệ hoàn thành: {stats.completionRate}%
                    </div>
                  )}
                </div>
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center text-white"
                  style={{ background: card.bgGradient }}
                >
                  <Icon className="text-lg" />
                </div>
              </div>
              <div className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
                <FiTrendingUp />
                <span>Xem chi tiết</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts - align card/layout style with CloudDashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
          <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-1.5">
              Ca chụp theo loại máy
            </h3>
            <FiActivity className="text-primary text-lg" />
          </div>
          <div className="w-full">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={stats.byModality || []}
                margin={{ top: 10, right: 10, left: 10, bottom: 36 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="modality" stroke="#64748b" height={36} tickMargin={6} />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem'
                  }} 
                />
                <Legend />
                <Bar dataKey="count" name="Số ca chụp" fill="#3b82f6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
          <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-1.5">
              Ca chụp theo cơ sở
            </h3>
            <FiActivity className="text-primary text-lg" />
          </div>
          <div className="w-full">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={stats.bySite || []}
                margin={{ top: 10, right: 10, left: 10, bottom: 36 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="site"
                  stroke="#64748b"
                  height={36}
                  tickMargin={6}
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem'
                  }} 
                />
                <Legend />
                <Bar dataKey="count" name="Số ca chụp" fill="#10b981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 pb-2 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-1.5">
              Ca chụp hàng ngày (7 ngày qua)
              <span className="text-xs text-slate-500 font-normal ml-1">
                {' '}
                - Theo ngày tạo trong hệ thống
              </span>
            </h3>
          </div>
          <FiTrendingUp className="text-primary text-lg" />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={stats.daily || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="date" 
              stroke="#64748b"
              tickFormatter={(value) => {
                if (!value) return '';
                return formatDateShort(value);
              }}
            />
            <YAxis stroke="#64748b" allowDecimals={false} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem'
              }}
              labelFormatter={(value) => {
                if (!value) return '';
                const date = new Date(value);
                if (isNaN(date.getTime())) return value;
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                const weekdays = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];
                const weekday = weekdays[date.getDay()];
                const months = ['tháng 1', 'tháng 2', 'tháng 3', 'tháng 4', 'tháng 5', 'tháng 6', 'tháng 7', 'tháng 8', 'tháng 9', 'tháng 10', 'tháng 11', 'tháng 12'];
                const monthName = months[date.getMonth()];
                return `${weekday}, ${day} ${monthName} ${year}`;
              }}
              formatter={(value) => [value, 'Số ca chụp']}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="count" 
              stroke="#3b82f6" 
              strokeWidth={3}
              dot={{ fill: '#3b82f6', r: 5 }}
              activeDot={{ r: 7 }}
              name="Số ca chụp"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Dashboard;

